/**
 * Drawing helpers for the Slingu mascot — shared between QuizRun and
 * FlappySlingu. Pure Canvas 2D, no framework.
 *
 * The mascot matches `SlinguAvatar.tsx`: gradient blue body with a highlight,
 * two eyes that blink, pink cheeks, a smile, and optional wings.
 */

export type SlinguDrawOpts = {
  /** radius in pixels (default 30) */
  radius?: number;
  /** rotation in radians (default 0) */
  angle?: number;
  /** vertical squash factor for running bounce [0..1]; 0 disables */
  squash?: number;
  /** eye blink [0..1] — 0 = open, 1 = fully closed */
  blink?: number;
  /** wing flap phase (radians). If omitted, no wings. */
  wingPhase?: number;
  /** wing spread amount (0 = folded, 1 = fully open) */
  wingOpen?: number;
  /** mouth mode */
  mouth?: 'smile' | 'open' | 'flat';
  /** Y coordinate of the ground plane — used to anchor a non-rotating
   * shadow beneath the character. If omitted, shadow sits immediately
   * below the body with no altitude cue. */
  groundY?: number;
};

export function drawSlingu(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  opts: SlinguDrawOpts = {},
) {
  const r = opts.radius ?? 30;
  const angle = opts.angle ?? 0;
  const squash = opts.squash ?? 0;
  const blink = Math.max(0, Math.min(1, opts.blink ?? 0));
  const wingPhase = opts.wingPhase;
  const wingOpen = opts.wingOpen ?? 1;
  const mouth = opts.mouth ?? 'smile';
  const groundY = opts.groundY;

  // Shadow — drawn in WORLD space (before any translate/rotate for the body),
  // so it never rotates with the character. If a groundY is provided, anchor
  // the shadow there and scale it by the character's altitude (higher = smaller
  // and more faded, like a real drop shadow).
  if (groundY !== undefined) {
    const altitude = Math.max(0, groundY - cy);
    const maxAlt = 400;
    const t = Math.min(1, altitude / maxAlt);
    const scale = 1 - t * 0.7;       // shrinks with height
    const alpha = 0.28 * (1 - t * 0.75); // fades with height
    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.fillStyle = 'rgb(20, 40, 80)';
    ctx.beginPath();
    ctx.ellipse(cx, groundY - 2, r * 0.85 * scale, 4 * scale, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  } else {
    // Fallback: small flat shadow directly below the body, no rotation.
    ctx.save();
    ctx.globalAlpha = 0.22;
    ctx.fillStyle = 'rgb(20, 40, 80)';
    ctx.beginPath();
    ctx.ellipse(cx, cy + r + 4, r * 0.7, 3.5, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  ctx.save();
  ctx.translate(cx, cy);
  ctx.rotate(angle);
  const sx = 1 + squash * 0.5;
  const sy = 1 - squash * 0.5;
  ctx.scale(sx, sy);

  // Wings
  if (wingPhase != null) {
    const lift = Math.sin(wingPhase) * 0.4 + 0.5;
    const spread = (0.4 + lift * 0.6) * wingOpen;
    drawWing(ctx, -r * 0.65, -r * 0.1, -1, spread);
    drawWing(ctx, r * 0.65, -r * 0.1, 1, spread);
  }

  // Body layers: dark outline → blue → light highlight → white spark
  ctx.fillStyle = 'rgb(20, 83, 163)';
  circle(ctx, 0, 1, r);
  ctx.fillStyle = 'rgb(28, 176, 246)';
  circle(ctx, 0, 0, r - 2);
  ctx.fillStyle = 'rgba(137, 218, 255, 0.65)';
  circle(ctx, -r * 0.23, -r * 0.23, r * 0.67);
  ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
  circle(ctx, -r * 0.36, -r * 0.4, r * 0.3);

  // Eyes
  const eyeR = r * 0.17;
  const eyeY = -r * 0.13;
  drawEye(ctx, -r * 0.27, eyeY, eyeR, blink);
  drawEye(ctx, r * 0.3, eyeY, eyeR, blink);

  // Mouth
  ctx.strokeStyle = 'rgb(20, 83, 163)';
  ctx.lineWidth = 2.5;
  ctx.lineCap = 'round';
  ctx.beginPath();
  if (mouth === 'smile') {
    ctx.moveTo(-r * 0.25, r * 0.3);
    ctx.quadraticCurveTo(0, r * 0.45, r * 0.25, r * 0.3);
  } else if (mouth === 'open') {
    ctx.ellipse(0, r * 0.35, r * 0.15, r * 0.1, 0, 0, Math.PI * 2);
  } else {
    ctx.moveTo(-r * 0.2, r * 0.35);
    ctx.lineTo(r * 0.2, r * 0.35);
  }
  ctx.stroke();

  // Cheeks
  ctx.fillStyle = 'rgba(255, 134, 208, 0.65)';
  circle(ctx, -r * 0.55, r * 0.23, r * 0.12);
  circle(ctx, r * 0.55, r * 0.23, r * 0.12);

  ctx.restore();
}

function circle(ctx: CanvasRenderingContext2D, x: number, y: number, r: number) {
  ctx.beginPath();
  ctx.arc(x, y, r, 0, Math.PI * 2);
  ctx.fill();
}

function drawEye(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, blink: number) {
  const openY = 1 - blink;
  ctx.save();
  ctx.translate(x, y);
  ctx.scale(1, Math.max(0.05, openY));
  // white
  ctx.fillStyle = 'rgb(255, 255, 255)';
  ctx.beginPath();
  ctx.ellipse(0, 0, r, r * 1.15, 0, 0, Math.PI * 2);
  ctx.fill();
  // pupil
  ctx.fillStyle = 'rgb(19, 31, 36)';
  ctx.beginPath();
  ctx.arc(r * 0.15, r * 0.05, r * 0.6, 0, Math.PI * 2);
  ctx.fill();
  // spark
  ctx.fillStyle = 'rgb(255, 255, 255)';
  ctx.beginPath();
  ctx.arc(-r * 0.1, -r * 0.25, r * 0.22, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

function drawWing(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  dir: number, // -1 left, 1 right
  spread: number,
) {
  ctx.save();
  ctx.translate(x, y);
  ctx.fillStyle = 'rgba(220, 240, 255, 0.95)';
  ctx.strokeStyle = 'rgba(20, 83, 163, 0.7)';
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.moveTo(0, -4);
  ctx.quadraticCurveTo(dir * 18, -8 + spread * 10, dir * 14, 6);
  ctx.quadraticCurveTo(dir * 4, 4, 0, -4);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();
  ctx.restore();
}

// ----- Shared world helpers ---------------------------------------------

export type DPRCanvas = {
  ctx: CanvasRenderingContext2D;
  logicalW: number;
  logicalH: number;
};

/**
 * Size a canvas to its parent's client box with device-pixel-ratio scaling.
 * Returns a context pre-scaled so all subsequent draw calls use logical
 * (CSS) pixels. Call on mount and on every window resize.
 */
export function sizeCanvas(
  canvas: HTMLCanvasElement,
  worldW: number,
  worldH: number,
): DPRCanvas {
  const dpr = Math.min(2, window.devicePixelRatio || 1);
  const parent = canvas.parentElement;
  const parentW = parent?.clientWidth ?? window.innerWidth;
  const parentH = parent?.clientHeight ?? window.innerHeight;
  // letterbox-fit
  const scale = Math.min(parentW / worldW, parentH / worldH);
  const cssW = worldW * scale;
  const cssH = worldH * scale;
  canvas.style.width = `${cssW}px`;
  canvas.style.height = `${cssH}px`;
  canvas.width = Math.floor(worldW * dpr);
  canvas.height = Math.floor(worldH * dpr);
  const ctx = canvas.getContext('2d')!;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  return { ctx, logicalW: worldW, logicalH: worldH };
}

export function drawBackground(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  groundY: number,
  hillsOffset: number,
  cloudOffset: number,
) {
  // Sky gradient — soft dawn-tinted blue
  const sky = ctx.createLinearGradient(0, 0, 0, groundY);
  sky.addColorStop(0, 'rgb(123, 183, 231)');
  sky.addColorStop(0.55, 'rgb(176, 218, 245)');
  sky.addColorStop(1, 'rgb(232, 242, 235)');
  ctx.fillStyle = sky;
  ctx.fillRect(0, 0, w, groundY);

  // Sun — soft glow + disk
  const sunX = w - Math.min(w * 0.22, 130);
  const sunY = Math.max(80, groundY * 0.16);
  const glow = ctx.createRadialGradient(sunX, sunY, 20, sunX, sunY, 180);
  glow.addColorStop(0, 'rgba(255, 240, 180, 0.55)');
  glow.addColorStop(1, 'rgba(255, 240, 180, 0)');
  ctx.fillStyle = glow;
  ctx.fillRect(sunX - 200, sunY - 200, 400, 400);
  ctx.fillStyle = 'rgb(255, 232, 150)';
  ctx.beginPath();
  ctx.arc(sunX, sunY, 44, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = 'rgba(255, 210, 120, 0.45)';
  ctx.beginPath();
  ctx.arc(sunX, sunY, 58, 0, Math.PI * 2);
  ctx.fill();

  // Far mountains — darkest silhouette layer behind everything else
  drawMountainRow(ctx, w, groundY - 160, groundY, 'rgb(108, 148, 180)', hillsOffset * 0.12, 120, 340);

  // Parallax hills (3 layers — back→front)
  drawHillRow(ctx, w, groundY - 100, groundY, 'rgb(168, 204, 188)', hillsOffset * 0.28, 140, 520);
  drawHillRow(ctx, w, groundY - 60, groundY, 'rgb(128, 180, 152)', hillsOffset * 0.55, 110, 420);
  drawHillRow(ctx, w, groundY - 20, groundY, 'rgb(92, 160, 118)', hillsOffset * 1.0, 80, 340);

  // Clouds — 4 layers at different depths & speeds
  ctx.fillStyle = 'rgba(255, 255, 255, 0.95)';
  drawCloudBelt(ctx, w, cloudOffset * 0.7, 60, 300, 6, 0.9);
  drawCloudBelt(ctx, w, cloudOffset * 1.0, 140, 280, 4, 0.75);
  drawCloudBelt(ctx, w, cloudOffset * 0.4, 30, 440, 3, 1);

  // Distant flying flock (3 v-shapes drifting slowly)
  drawFlock(ctx, w, cloudOffset * 0.35);
}

function drawCloudBelt(
  ctx: CanvasRenderingContext2D,
  w: number,
  offset: number,
  baseY: number,
  spacing: number,
  count: number,
  scale: number,
) {
  for (let i = 0; i < count; i++) {
    const spread = w + spacing;
    const cx = ((i * spacing - offset) % spread + spread) % spread - spacing / 2;
    const cy = baseY + ((i * 37) % 40);
    drawCloud(ctx, cx, cy, scale);
  }
}

function drawCloud(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  scale = 1,
) {
  ctx.save();
  ctx.translate(x, y);
  ctx.scale(scale, scale);
  ctx.beginPath();
  ctx.arc(0, 0, 14, 0, Math.PI * 2);
  ctx.arc(18, -6, 18, 0, Math.PI * 2);
  ctx.arc(38, -2, 16, 0, Math.PI * 2);
  ctx.arc(55, 4, 12, 0, Math.PI * 2);
  ctx.fill();
  // subtle shading on bottom
  ctx.fillStyle = 'rgba(150, 180, 210, 0.25)';
  ctx.beginPath();
  ctx.ellipse(26, 10, 30, 5, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

function drawFlock(ctx: CanvasRenderingContext2D, w: number, offset: number) {
  ctx.save();
  ctx.strokeStyle = 'rgba(35, 80, 115, 0.55)';
  ctx.lineWidth = 1.5;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  const spacing = 280;
  for (let i = 0; i < 3; i++) {
    const spread = w + spacing;
    const cx = ((i * spacing - offset) % spread + spread) % spread - 60;
    const cy = 180 + (i % 2) * 30;
    // Simple v-shape seagull
    ctx.beginPath();
    ctx.moveTo(cx - 8, cy + 2);
    ctx.quadraticCurveTo(cx - 3, cy - 4, cx, cy);
    ctx.quadraticCurveTo(cx + 3, cy - 4, cx + 8, cy + 2);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(cx + 16, cy + 4);
    ctx.quadraticCurveTo(cx + 20, cy, cx + 23, cy + 3);
    ctx.quadraticCurveTo(cx + 26, cy, cx + 30, cy + 4);
    ctx.stroke();
  }
  ctx.restore();
}

function drawMountainRow(
  ctx: CanvasRenderingContext2D,
  w: number,
  yTop: number,
  yBot: number,
  color: string,
  offset: number,
  h: number,
  width: number,
) {
  ctx.fillStyle = color;
  const step = width;
  for (let i = -1; i * step < w + width; i++) {
    const x = i * step - (offset % step);
    ctx.beginPath();
    ctx.moveTo(x, yBot);
    ctx.lineTo(x + width * 0.3, yTop + h * 0.25);
    ctx.lineTo(x + width * 0.5, yTop);
    ctx.lineTo(x + width * 0.7, yTop + h * 0.35);
    ctx.lineTo(x + width, yBot);
    ctx.closePath();
    ctx.fill();
    // Snow cap
    ctx.fillStyle = 'rgba(255, 255, 255, 0.6)';
    ctx.beginPath();
    ctx.moveTo(x + width * 0.42, yTop + h * 0.1);
    ctx.lineTo(x + width * 0.5, yTop);
    ctx.lineTo(x + width * 0.58, yTop + h * 0.14);
    ctx.closePath();
    ctx.fill();
    ctx.fillStyle = color;
  }
}

function drawHillRow(
  ctx: CanvasRenderingContext2D,
  w: number,
  yTop: number,
  yBot: number,
  color: string,
  offset: number,
  h: number,
  width: number,
) {
  ctx.fillStyle = color;
  const step = width;
  for (let i = -1; i * step < w + width; i++) {
    const x = i * step - (offset % step);
    ctx.beginPath();
    ctx.moveTo(x, yBot);
    ctx.quadraticCurveTo(x + width / 2, yTop - h + (yBot - yTop), x + width, yBot);
    ctx.closePath();
    ctx.fill();
  }
}

export function drawGround(
  ctx: CanvasRenderingContext2D,
  w: number,
  y: number,
  h: number,
  scrollX: number,
) {
  // Dirt with subtle vertical shading
  const grad = ctx.createLinearGradient(0, y, 0, y + h);
  grad.addColorStop(0, 'rgb(138, 102, 66)');
  grad.addColorStop(1, 'rgb(104, 76, 48)');
  ctx.fillStyle = grad;
  ctx.fillRect(0, y, w, h);
  // Grass line
  ctx.fillStyle = 'rgb(92, 160, 118)';
  ctx.fillRect(0, y, w, 8);
  ctx.fillStyle = 'rgba(72, 126, 92, 0.55)';
  ctx.fillRect(0, y + 6, w, 3);

  // Grass tufts along the surface
  const tuftSpacing = 54;
  for (let i = 0; i < 24; i++) {
    const sx = ((i * tuftSpacing - scrollX) % (w + tuftSpacing) + (w + tuftSpacing)) % (w + tuftSpacing) - tuftSpacing / 2;
    drawGrassTuft(ctx, sx, y + 6, (i % 3 === 0) ? 1.2 : 0.8);
  }

  // Flower specks
  const flowerSpacing = 140;
  for (let i = 0; i < 10; i++) {
    const sx = ((i * flowerSpacing - scrollX * 1.05) % (w + flowerSpacing) + (w + flowerSpacing)) % (w + flowerSpacing) - 60;
    const color = i % 2 === 0 ? 'rgb(255, 182, 193)' : 'rgb(255, 236, 120)';
    drawFlower(ctx, sx, y + 10, color);
  }

  // Pebbles embedded in dirt
  const pebbleSpacing = 70;
  ctx.fillStyle = 'rgba(80, 55, 30, 0.55)';
  for (let i = 0; i < 18; i++) {
    const sx = ((i * pebbleSpacing - scrollX * 0.9) % (w + pebbleSpacing) + (w + pebbleSpacing)) % (w + pebbleSpacing) - 40;
    const ly = y + 20 + (i % 3) * 12;
    ctx.beginPath();
    ctx.ellipse(sx, ly, 5, 2.5, 0, 0, Math.PI * 2);
    ctx.fill();
  }
}

function drawGrassTuft(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  scale: number,
) {
  ctx.save();
  ctx.translate(x, y);
  ctx.scale(scale, scale);
  ctx.fillStyle = 'rgb(92, 160, 118)';
  ctx.beginPath();
  ctx.moveTo(-6, 0);
  ctx.quadraticCurveTo(-4, -10, -2, 0);
  ctx.closePath();
  ctx.fill();
  ctx.beginPath();
  ctx.moveTo(-2, 0);
  ctx.quadraticCurveTo(0, -14, 2, 0);
  ctx.closePath();
  ctx.fill();
  ctx.beginPath();
  ctx.moveTo(2, 0);
  ctx.quadraticCurveTo(4, -9, 6, 0);
  ctx.closePath();
  ctx.fill();
  ctx.restore();
}

function drawFlower(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  petalColor: string,
) {
  ctx.save();
  ctx.translate(x, y);
  // Stem
  ctx.strokeStyle = 'rgb(72, 126, 92)';
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.moveTo(0, 0);
  ctx.lineTo(0, -12);
  ctx.stroke();
  // Petals (5)
  ctx.fillStyle = petalColor;
  for (let i = 0; i < 5; i++) {
    const a = (i / 5) * Math.PI * 2;
    ctx.beginPath();
    ctx.arc(Math.cos(a) * 3, -12 + Math.sin(a) * 3, 2.5, 0, Math.PI * 2);
    ctx.fill();
  }
  // Center
  ctx.fillStyle = 'rgb(255, 200, 0)';
  ctx.beginPath();
  ctx.arc(0, -12, 1.8, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}
